import { http } from "./client";

// Learning (Admin/HR): skills catalog, seminars, participants and attendance.
const data = (r) => r.data;
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && v !== false,
    ),
  );

export const skillApi = {
  // GET /learning/skills?search&includeInactive → Skill[]
  getAll: (params = {}) =>
    http.get("/learning/skills", { params: clean(params) }).then(data),
  create: (body) => http.post("/learning/skills", body).then(data),
  // { name?, category?, description?, isActive? }
  update: (id, body) => http.patch(`/learning/skills/${id}`, body).then(data),
  // 409 when the skill is used → offer "Deactivate"
  remove: (id) => http.delete(`/learning/skills/${id}`).then(() => id),
};

export const seminarApi = {
  /**
   * GET /learning/seminars → { data, total, page, limit }
   * Each: counts { total, PENDING, ATTENDED, ABSENT, EXCUSED }, seatsLeft, phase (UPCOMING|ONGOING|ENDED), skills[]
   * filters: status, from, to, skillId, employeeId, search, page, limit
   */
  getList: (filters = {}) =>
    http.get("/learning/seminars", { params: clean(filters) }).then(data),
  // Venue lookup (OpenStreetMap): → { results: [{ latitude, longitude, label, mapUrl }], failed }
  geocode: (address) =>
    http.get("/learning/seminars/geocode", { params: { address } }).then(data),
  // → seminar with participants[]
  getById: (id) => http.get(`/learning/seminars/${id}`).then(data),
  create: (body) => http.post("/learning/seminars", body).then(data),
  update: (id, body) => http.patch(`/learning/seminars/${id}`, body).then(data),
  cancel: (id, reason) =>
    http.post(`/learning/seminars/${id}/cancel`, { reason }).then(data),
  // Unmarked participants become ABSENT
  complete: (id) => http.post(`/learning/seminars/${id}/complete`).then(data),
  remove: (id) => http.delete(`/learning/seminars/${id}`).then(() => id),

  // { employeeIds?, branchIds?, departmentIds?, jobTitleIds? } → { added, alreadyAssigned, notEligible[] }
  assign: (id, body) =>
    http.post(`/learning/seminars/${id}/participants`, clean(body)).then(data),
  removeParticipant: (id, employeeId) =>
    http
      .delete(`/learning/seminars/${id}/participants/${employeeId}`)
      .then(() => employeeId),
  // { entries: [{ employeeId, status: ATTENDED|ABSENT|EXCUSED|PENDING, remarks? }] } (only after it starts)
  saveAttendance: (id, entries) =>
    http.put(`/learning/seminars/${id}/attendance`, { entries }).then(data),

  // GET /learning/employees/:employeeId/record → totals, skills, attended…
  getEmployeeRecord: (employeeId) =>
    http.get(`/learning/employees/${employeeId}/record`).then(data),

  /* QR check-in: { enabled, code, checkInPath, opensAt, closesAt } */
  getCheckInQr: (id) =>
    http.get(`/learning/seminars/${id}/check-in-qr`).then(data),
  // Turn on, or replace the code (an old printed QR stops working)
  enableCheckInQr: (id) =>
    http.post(`/learning/seminars/${id}/check-in-qr`).then(data),
  disableCheckInQr: (id) =>
    http.delete(`/learning/seminars/${id}/check-in-qr`).then(data),

  /**
   * Attendance sheet (Excel/CSV): preview, or apply with apply=true →
   * { applied, updated, counts, columns, rowsRead, changes, unchanged, notParticipants, duplicates, badStatus, notOnSheet }
   */
  uploadAttendanceSheet: (id, file, apply) => {
    const form = new FormData();
    form.append("file", file);
    return http
      .post(`/learning/seminars/${id}/attendance-sheet`, form, {
        params: apply ? { apply: true } : {},
      })
      .then(data);
  },

  /** The seminar's sheet (.xlsx) pre-filled with its participants → { blob, fileName } */
  attendanceTemplate: (id) =>
    http
      .get(`/learning/seminars/${id}/attendance-sheet/template`, {
        responseType: "blob",
      })
      .then((r) => {
        const header = r.headers?.["content-disposition"] || "";
        const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
        return {
          blob: r.data,
          fileName: m ? decodeURIComponent(m[1]) : "attendance.xlsx",
        };
      }),

  /* Files: scanned signed sheets, photos (category ATTENDANCE_SHEET | PHOTO | OTHER) */
  getFiles: (id) => http.get(`/learning/seminars/${id}/files`).then(data),
  uploadFile: (id, { category, remarks, file }) => {
    const form = new FormData();
    form.append("category", category);
    if (remarks) form.append("remarks", remarks);
    form.append("file", file);
    return http.post(`/learning/seminars/${id}/files`, form).then(data);
  },
  removeFile: (id, fileId) =>
    http.delete(`/learning/seminars/${id}/files/${fileId}`).then(() => fileId),
};

/* Training bonds (id = the participant) */
export const bondApi = {
  // ?status&search&seminarId&page&limit → { data, total, totals: { active, separated, owed }, page, limit }
  getList: (params = {}) =>
    http.get("/learning/bonds", { params: clean(params) }).then(data),
  // { cost?, months? } → { id, acceptanceCleared }. Clears any acceptance; 409 once the bond has started.
  adjust: (id, body) => http.patch(`/learning/bonds/${id}`, body).then(data),
  // { reason } (required): nothing is owed
  release: (id, reason) =>
    http.post(`/learning/bonds/${id}/release`, { reason }).then(data),
  // PDF or image, 10 MB; replaces any earlier copy
  uploadSignedCopy: (id, file) => {
    const form = new FormData();
    form.append("file", file);
    return http.post(`/learning/bonds/${id}/signed-copy`, form).then(data);
  },
  // → [{ companyId, branchId | null, paybackRule }]
  getDefaults: (companyId) =>
    http.get("/learning/bond-defaults", { params: { companyId } }).then(data),
  // { companyId, branchId?, paybackRule: REDUCING | FULL }; no branchId = the company's default
  setDefault: (body) =>
    http.put("/learning/bond-defaults", clean(body)).then(data),
};
